"use client";

/* eslint-disable @next/next/no-img-element */

import * as React from "react";

import { cn } from "@/lib/utils";

type ImageStatus = "loading" | "loaded" | "error";

type AvatarContextValue = {
  status: ImageStatus;
  setStatus: (status: ImageStatus) => void;
  hasSrc: boolean;
  setHasSrc: (has: boolean) => void;
};

const AvatarContext = React.createContext<AvatarContextValue | null>(null);

const Avatar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const [status, setStatus] = React.useState<ImageStatus>("loading");
  const [hasSrc, setHasSrc] = React.useState(false);

  return (
    <AvatarContext.Provider value={{ status, setStatus, hasSrc, setHasSrc }}>
      <div
        ref={ref}
        className={cn(
          "relative flex h-9 w-9 shrink-0 overflow-hidden rounded-full bg-muted",
          className,
        )}
        style={{ overflow: "hidden" }}
        {...props}
      />
    </AvatarContext.Provider>
  );
});
Avatar.displayName = "Avatar";

const AvatarImage = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement>
>(({ className, alt = "", style, onLoad, onError, src, ...props }, ref) => {
  const context = React.useContext(AvatarContext);

  // Reset status when src changes
  React.useEffect(() => {
    if (src) {
      context?.setHasSrc(true);
      context?.setStatus("loading");
    } else {
      context?.setHasSrc(false);
      context?.setStatus("error");
    }
  }, [src]);

  const handleLoad = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      context?.setStatus("loaded");
      onLoad?.(e);
    },
    [context, onLoad]
  );

  const handleError = React.useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      context?.setStatus("error");
      onError?.(e);
    },
    [context, onError]
  );

  if (!src || context?.status === "error") {
    return null;
  }

  return (
    <img
      ref={ref}
      src={src}
      className={cn(
        "absolute inset-0 h-full w-full object-cover object-center",
        context?.status === "loaded" ? "opacity-100" : "opacity-0",
        "transition-opacity duration-150",
        className
      )}
      alt={alt}
      style={{ objectFit: "cover", objectPosition: "center", ...style }}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
});
AvatarImage.displayName = "AvatarImage";

const AvatarFallback = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & { showSkeleton?: boolean }
>(({ className, showSkeleton = true, children, ...props }, ref) => {
  const context = React.useContext(AvatarContext);

  // Show skeleton while loading (if image src exists)
  if (context?.hasSrc && context?.status === "loading" && showSkeleton) {
    return (
      <span
        ref={ref}
        className={cn(
          "flex h-full w-full items-center justify-center rounded-full animate-pulse bg-muted",
          className,
        )}
        {...props}
      />
    );
  }

  // Show fallback content when no image or error
  if (!context?.hasSrc || context?.status === "error") {
    return (
      <span
        ref={ref}
        className={cn(
          "flex h-full w-full items-center justify-center rounded-full bg-slate-800 text-xs font-medium text-slate-50",
          className,
        )}
        {...props}
      >
        {children}
      </span>
    );
  }

  // Hide when image is loaded
  return null;
});
AvatarFallback.displayName = "AvatarFallback";

export { Avatar, AvatarImage, AvatarFallback };
